import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ProjectCard from "./ProjectCard";
import ProjectModal from "../ProjectModal";
import CreateProjectModal from "./CreateProjectModal";

interface Project {
  id: string;
  title: string;
  description: string | null;
  media_url: string | null;
  media_type: string | null;
  created_at: string;
}

interface PortfolioListProps {
  userId: string;
  isEditing?: boolean;
  onComplete?: () => void;
}

const PortfolioList = ({ userId, isEditing = false, onComplete }: PortfolioListProps) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchProjects();
  }, [userId]);

  const fetchProjects = async () => {
    try {
      const { data: portfolios } = await supabase
        .from('portfolios')
        .select('id')
        .eq('user_id', userId);

      if (portfolios && portfolios.length > 0) {
        const { data, error } = await supabase
          .from('portfolio_items')
          .select('*')
          .eq('portfolio_id', portfolios[0].id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setProjects(data || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        title: "Error",
        description: "Failed to load projects",
        variant: "destructive",
      });
    }
  };

  const handleProjectCreated = (newProject: Project) => {
    setProjects([newProject, ...projects]);
    setIsCreateModalOpen(false);
    if (onComplete) {
      onComplete();
    }
  };

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  return (
    <div>
      <Button
        data-create-project
        className="hidden"
        onClick={() => setIsCreateModalOpen(true)}
      >
        Create Project
      </Button>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            onClick={() => handleProjectClick(project)}
            isEditing={isEditing}
          />
        ))}
      </div>

      {selectedProject && (
        <ProjectModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          project={selectedProject}
        />
      )}

      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        userId={userId}
        onProjectCreated={handleProjectCreated}
      />
    </div>
  );
};

export default PortfolioList;